import { Child } from '../types';
import { getSupabase } from './supabaseClient';

const TABLE = 'children';

function toChild(row: any): Child {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    authorName: row.author_name || row.name,
    age: row.age,
    sex: row.sex || 'unspecified',
    appearance: row.appearance || '',
    photoBase64: row.photo_base64 || '',
  };
}

export const childrenService = {
  async getChildren(userId: string): Promise<Child[]> {
    const { data, error } = await getSupabase()
      .from(TABLE)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (error) { console.error(error); return []; }
    return (data || []).map(toChild);
  },

  async createChild(userId: string, child: Omit<Child, 'id' | 'userId'>): Promise<Child> {
    const { data, error } = await getSupabase()
      .from(TABLE)
      .insert({
        user_id: userId,
        name: child.name,
        author_name: child.authorName,
        age: child.age,
        sex: child.sex,
        appearance: child.appearance,
        photo_base64: child.photoBase64 || null,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return toChild(data);
  },

  async updateChild(child: Child): Promise<void> {
    const { error } = await getSupabase()
      .from(TABLE)
      .update({
        name: child.name,
        author_name: child.authorName,
        age: child.age,
        sex: child.sex,
        appearance: child.appearance,
        photo_base64: child.photoBase64 || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', child.id)
      .eq('user_id', child.userId);
    if (error) throw new Error(error.message);
  },

  async deleteChild(childId: string): Promise<void> {
    const { error } = await getSupabase()
      .from(TABLE)
      .delete()
      .eq('id', childId);
    if (error) throw new Error(error.message);
  },

  /** Migrate existing child_profiles row into children table (one-time, idempotent). */
  async migrateFromLegacyProfile(userId: string): Promise<Child | null> {
    const existing = await this.getChildren(userId);
    if (existing.length > 0) return existing[0];

    const { data } = await getSupabase()
      .from('child_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (!data) return null;

    return this.createChild(userId, {
      name: data.name,
      authorName: data.author_name || data.name,
      age: data.age,
      sex: data.sex || 'unspecified',
      appearance: data.appearance || '',
      photoBase64: data.photo_base64 || '',
    });
  },
};
